import React from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { ArrowRight } from '@phosphor-icons/react';

export function getCategoryTitle(cat, lang) {
  if (lang === 'fr' && cat.title_fr) return cat.title_fr;
  if (lang === 'es' && cat.title_es) return cat.title_es;
  if (lang === 'ar' && cat.title_ar) return cat.title_ar;
  return cat.title_en;
}

export function getCategoryDesc(cat, lang) {
  if (lang === 'fr' && cat.desc_fr) return cat.desc_fr;
  if (lang === 'es' && cat.desc_es) return cat.desc_es;
  if (lang === 'ar' && cat.desc_ar) return cat.desc_ar;
  return cat.desc_en;
}

export const CATEGORIES = [
  {
    id: "food",
    key: "Food & Beverages",
    title_en: "Food & Beverages",
    title_tr: "Gıda & İçecek",
    title_fr: "Alimentation & boissons",
    title_es: "Alimentación y bebidas",
    title_ar: "الأغذية والمشروبات",
    desc_en: "Pasta, flour, sunflower oil, bulgur, semolina, fruit juices, energy drinks, tomato paste, confectionery, grape leaves & pickles",
    desc_tr: "Makarna, un, ayçiçek yağı, bulgur, irmik, meyve suları, enerji içeceği, salça, şekerleme, asma yaprağı ve turşu",
    desc_fr: "Pâtes, farine, huile de tournesol, boulgour, semoule, jus de fruits, boissons énergisantes, concentré de tomate, confiseries, feuilles de vigne et cornichons",
    desc_es: "Pasta, harina, aceite de girasol, bulgur, sémola, zumos, bebidas energéticas, tomate concentrado, confitería, hojas de parra y encurtidos",
    desc_ar: "معكرونة، دقيق، زيت دوار الشمس، برغل، سميد، عصائر، مشروبات طاقة، معجون طماطم، حلويات، ورق عنب ومخللات",
    visualBg: "linear-gradient(145deg, #431407 0%, #9a3412 40%, #ea580c 100%)",
    span: "md:col-span-2 md:row-span-2",
    height: "h-64 md:h-full min-h-[320px]"
  },
  {
    id: "construction",
    key: "Construction & Building",
    title_en: "Construction & Building",
    title_tr: "İnşaat & Yapı",
    title_fr: "Construction & bâtiment",
    title_es: "Construcción y edificación",
    title_ar: "البناء والتشييد",
    desc_en: "Marble, ceramic tile, cement, paint and coating solutions",
    desc_tr: "Mermer, seramik karo, çimento, boya ve kaplama çözümleri",
    desc_fr: "Marbre, carrelage, ciment, peintures et solutions de revêtement",
    desc_es: "Mármol, baldosa cerámica, cemento, pintura y soluciones de revestimiento",
    desc_ar: "رخام، بلاط سيراميك، أسمنت، دهانات وحلول طلاء",
    visualBg: "linear-gradient(145deg, #0f172a 0%, #334155 55%, #475569 100%)",
    span: "",
    height: "h-64"
  },
  {
    id: "cleaning",
    key: "Household Cleaning",
    title_en: "Household Cleaning",
    title_tr: "Ev Temizlik",
    title_fr: "Entretien ménager",
    title_es: "Limpieza del hogar",
    title_ar: "تنظيف المنزل",
    desc_en: "Laundry detergent, dishwashing products, liquid soap, cleaners and softeners",
    desc_tr: "Çamaşır deterjanı, bulaşık ürünleri, sıvı sabun, temizleyiciler ve yumuşatıcılar",
    desc_fr: "Lessive, produits vaisselle, savon liquide, nettoyants et adoucissants",
    desc_es: "Detergente ropa, lavavajillas, jabón líquido, limpiadores y suavizantes",
    desc_ar: "منظف غسيل، أطباق، صابون سائل، مطهرات وملينات",
    visualBg: "linear-gradient(145deg, #1e3a5f 0%, #0d9488 50%, #14b8a6 100%)",
    span: "",
    height: "h-64"
  },
  {
    id: "personal_care",
    key: "Personal Care",
    title_en: "Personal Care",
    title_tr: "Kişisel Bakım",
    title_fr: "Soins personnels",
    title_es: "Cuidado personal",
    title_ar: "العناية الشخصية",
    desc_en: "Baby diaper, wet wipes, toilet paper, paper towel and sanitary products",
    desc_tr: "Bebek bezi, ıslak mendil, tuvalet kağıdı, kağıt havlu ve hijyen ürünleri",
    desc_fr: "Couches bébé, lingettes, papier toilette, essuie-tout et produits d’hygiène",
    desc_es: "Pañales, toallitas húmedas, papel higiénico, papel de cocina e higiene",
    desc_ar: "حفاضات، مناديل مبللة، ورق تنظيف، مناشف ورقية ومنتجات صحية",
    visualBg: "linear-gradient(145deg, #4c1d95 0%, #7c3aed 45%, #c026d3 100%)",
    span: "md:col-span-2",
    height: "h-64"
  }
];

/**
 * Üst navigasyon “Categories” mega menüsü sol şerit sırası (anasayfa kategori kartları `CATEGORIES` sırasından bağımsız).
 * Food içindeki alt kategori grid sırası `catalogTaxonomy.FOOD_MEGA_MENU_ORDER` ile belirlenir.
 */
export const MEGA_MENU_SIDEBAR_CATEGORY_IDS = ['food', 'cleaning', 'personal_care', 'construction'];

export default function CategoryGrid({ onCategorySelect }) {
  const { t, lang, tl } = useLanguage();

  return (
    <section className="px-5 md:px-10 lg:px-16 py-20 md:py-24 bg-white" data-testid="category-grid">
      <div className="max-w-7xl mx-auto">
        <div className="mb-12 flex flex-col md:flex-row md:items-end md:justify-between gap-4">
          <div>
            <span className="inline-block px-4 py-1.5 rounded-full bg-[#b4533c]/10 text-[#b4533c] text-xs font-semibold tracking-wider uppercase mb-4 font-sans">
            {tl('Categories', 'Categories', 'Categorias', 'الفئات')}
            </span>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-2">{t('browseCategories')}</h2>
            <p className="text-gray-600 max-w-xl font-sans">{t('categoriesSub')}</p>
          </div>
          <button
            onClick={() => onCategorySelect && onCategorySelect({ category: "", subcategory: "", search: "" })}
            className="text-sm text-[#b4533c] font-semibold font-sans hover:underline"
            data-testid="all-categories-button"
          >
            {tl('All Categories', 'Toutes les categories', 'Todas las categorias', 'جميع الفئات')}
          </button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {CATEGORIES.map(cat => (
            <button
              key={cat.id}
              onClick={() => onCategorySelect(cat.key)}
              className={`${cat.span} ${cat.height} relative overflow-hidden rounded-2xl group text-left image-shine`}
              data-testid={`category-${cat.id}`}
            >
              <div
                className="absolute inset-0 w-full h-full transition-transform duration-700 ease-out group-hover:scale-105"
                style={{ background: cat.visualBg }}
                aria-hidden
              />
              <div className="category-overlay absolute inset-0 group-hover:opacity-90 transition-opacity duration-500" />

              <div className="absolute top-4 right-4 w-10 h-10 rounded-full glass flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all duration-300 translate-x-2 group-hover:translate-x-0">
                <ArrowRight size={16} className="text-white" weight="bold" />
              </div>

              <div className="relative z-10 h-full flex flex-col justify-end p-6 md:p-7">
                <h3 className="text-xl md:text-2xl font-bold text-white mb-1.5 font-serif">
                  {getCategoryTitle(cat, lang)}
                </h3>
                <p className="text-sm text-white/90 mb-4 line-clamp-2 font-sans max-w-sm">
                  {getCategoryDesc(cat, lang)}
                </p>
                <span className="inline-flex items-center gap-2 text-xs font-semibold text-[#fca5a5] w-fit font-sans tracking-wider uppercase group-hover:gap-3 transition-all">
                  {t('viewAll')} <ArrowRight size={12} weight="bold" />
                </span>
              </div>
            </button>
          ))}
        </div>
      </div>
    </section>
  );
}
